<template>
  <ProductDetailsModal
    v-if="
      detailsModalVisible &&
      selectedSubmission?.JobType.toLowerCase().includes('products')
    "
    :job-id="selectedSubmission.JobId"
    @closeModal="detailsModalVisible = false"
  />
  <IngredientDetailsModal
    v-if="
      detailsModalVisible &&
      selectedSubmission?.JobType.toLowerCase().includes('ingredients')
    "
    :job-id="selectedSubmission.JobId"
    @closeModal="detailsModalVisible = false"
  />

  <div v-if="submissions.length == 0 && !loading" class="no-results">
    <span>No results found</span>
  </div>
  <ListView
    class="submission-list"
    :data-items="submissions"
    item="submissionTile"
    footer="submissionListFooter"
    @scroll="scrollHandler($event as unknown as Event)"
    data-cy="submission-list"
  >
    <template v-slot:submissionTile="{ props }">
      <SubmissionTile
        :submission="props.dataItem"
        @showDetails="showDetailsModal"
      />
    </template>
    <template v-slot:submissionListFooter>
      <Loader
        v-if="loading"
        class="submissions-loader"
        size="large"
        type="pulsing"
        data-cy="submissions-loader"
      />
    </template>
  </ListView>
</template>

<script setup lang="ts">
import SubmissionTile from "./SubmissionTile.vue";
import { computed, ref, watchEffect } from "vue";
import { ListView } from "@progress/kendo-vue-listview";
import { Loader } from "@progress/kendo-vue-indicators";
import type { SubmissionsTable } from "@/Domain/SubmissionsTable";
import ProductDetailsModal from "./ProductDetailsModal.vue";
import IngredientDetailsModal from "./IngredientDetailsModal.vue";

const props = defineProps<{
  submissions: SubmissionsTable[];
  totalSubmissions: number;
  defaultTake?: number;
  skip: number;
  loading: boolean;
}>();

const emit = defineEmits<{
  (e: "submissionsChanged", skip: number, take: number): void;
}>();

const take = ref<number>(props.defaultTake ?? 10);
const skip = ref<number>(props.skip);
const detailsModalVisible = ref<boolean>(false);
const selectedSubmission = ref<SubmissionsTable | null>(null);

const loadScrollThreshold = 100;

const isProductSubmissionSelected = computed(() => {
  return selectedSubmission.value?.JobType.includes("Products");
});

const showDetailsModal = (submission: SubmissionsTable) => {
  selectedSubmission.value = submission;
  detailsModalVisible.value = true;
};

const scrollHandler = function (e: Event) {
  const target = e.target as HTMLDivElement | null;
  if (!target) return;
  if (
    target.scrollTop + loadScrollThreshold <
    target.scrollHeight - target.clientHeight
  )
    return;
  if (skip.value + take.value >= props.totalSubmissions) return;
  if (props.loading) return;

  skip.value = skip.value + take.value;
  emit("submissionsChanged", skip.value, take.value);
};

watchEffect(() => {
  skip.value = props.skip;
});
</script>

<style lang="scss">
.submission-list {
  background: none !important;
  border: none !important;
  max-height: 70vh;
  margin-top: 16px;

  .k-listview-content {
    padding: 15px 15px;
  }

  .k-listview-footer {
    border-top-width: 0 !important; 
  }
}
.submissions-loader {
  display: flex !important;
  justify-content: center;
  margin: 32px 0;
}

.no-results {
  display: flex;
  justify-content: center;
  margin: 32px 0;
  font-size: 1.5rem;
  font-weight: 500;
  color: #6c757d;
}
</style>
